import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, EmptyState, Spinner, TextField, Typography } from '@glplus/glpweb-dsl';
import {
  AdminApiError,
  adminApiClient,
  type VendorCertificationEvidence,
  type VendorCertificationReviewItem,
} from '../../lib/adminApiClient';
import { DANGER_BTN, NEUTRAL_BTN } from '../../lib/buttonStyles';
import { Actions, Card, Field, MUTED, PRIMARY_BTN, Panel, SideBySide, day } from './parts';

const CERTIFICATION_LABELS: Record<string, string> = {
  halal: 'Halal',
  kosher: 'Kosher',
  organic_handler: 'Organic handler',
  gluten_free_facility: 'Gluten-free facility',
  wic: 'WIC',
  animal_welfare: 'Animal welfare',
  custom: 'Custom certification',
};

export function certificationLabel(item: Pick<VendorCertificationReviewItem, 'certificationKey' | 'customLabel'>): string {
  return item.certificationKey === 'custom'
    ? item.customLabel || CERTIFICATION_LABELS.custom
    : CERTIFICATION_LABELS[item.certificationKey] || item.certificationKey;
}

export function CertificationsTab({ canReview, onCount }: { canReview: boolean; onCount: (n: number) => void }) {
  const [items, setItems] = useState<VendorCertificationReviewItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const result = await adminApiClient.listVendorCertifications('pending');
      setItems(result.items);
      onCount(result.count);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load certification proofs');
    }
  }, [onCount]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!items) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><Spinner color="brand" /></Box>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {flash && <Alert severity="success">{flash}</Alert>}
      {items.length === 0 ? (
        <EmptyState icon="verified" title="No certification proofs waiting" body="Private location evidence shows here until a staff reviewer verifies or rejects it." />
      ) : items.map((item) => (
        <CertificationCard
          key={item.certificationId}
          item={item}
          canReview={canReview}
          onDone={(message) => {
            setFlash(message);
            void load();
          }}
        />
      ))}
    </Box>
  );
}

function CertificationCard({ item, canReview, onDone }: { item: VendorCertificationReviewItem; canReview: boolean; onDone: (message: string) => void }) {
  const [evidence, setEvidence] = useState<VendorCertificationEvidence | null>(null);
  const [reason, setReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const label = certificationLabel(item);

  const run = async (work: () => Promise<unknown>, done: string) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      onDone(done);
    } catch (err) {
      setError(err instanceof AdminApiError ? err.message : 'That did not go through');
    } finally {
      setBusy(false);
    }
  };

  const loadEvidence = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await adminApiClient.getVendorCertification(item.certificationId);
      setEvidence(result.evidence);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Private evidence could not be opened');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card testId={`vendor-certification-${item.certificationId}`}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography component="h2" sx={{ fontSize: 18, fontWeight: 700 }}>{label}</Typography>
        <Chip label={item.vendor.businessName} size="small" />
        <Chip label="Private proof" size="small" />
      </Box>
      <Typography sx={{ fontSize: 13, color: MUTED }}>Submitted {day(item.submittedAt)} · expires {day(item.expiresOn)}</Typography>

      <SideBySide
        leftTitle="What the vendor sent"
        rightTitle="Staff checks"
        left={(
          <>
            <Field label="Location">{item.store.name}{item.store.formattedAddress ? ` — ${item.store.formattedAddress}` : ''}</Field>
            <Field label="Issuer">{item.issuerName}</Field>
            <Field label="Certificate reference">{item.certificateRef}</Field>
            <Field label="Issued">{day(item.issuedOn)}</Field>
            <Field label="File">{item.proofFileName} · {Math.max(1, Math.round(item.proofByteSize / 1024))} KB</Field>
          </>
        )}
        right={(
          <>
            <Field label="Check">The issuer and certificate belong to this location.</Field>
            <Field label="Check">The proof is readable and has not expired.</Field>
            <Field label="Privacy">The private object key is never shown; the review link expires after 15 minutes.</Field>
            {!evidence ? (
              <Button ariaLabel={`Load private proof for ${label}`} buttonText={busy ? 'Loading…' : 'Load private proof'} variant="outlined" size="small" disabled={busy} sx={NEUTRAL_BTN} onClick={() => void loadEvidence()} />
            ) : (
              <Box component="a" href={evidence.reviewUrl} target="_blank" rel="noopener noreferrer" sx={{ color: '#7A3E00', fontWeight: 700 }}>
                Open private evidence (15-minute link)
              </Box>
            )}
          </>
        )}
      />

      {error && <Box sx={{ mt: 2 }}><Alert severity="error">{error}</Alert></Box>}
      {canReview && (
        <Actions>
          <Button
            ariaLabel={`Verify ${label}`}
            buttonText={busy ? 'Working…' : 'Verify certification'}
            variant="contained"
            size="small"
            disabled={busy || !evidence}
            sx={PRIMARY_BTN}
            onClick={() => void run(() => adminApiClient.decideVendorCertification(item.certificationId, { decision: 'verified' }), `${label} is verified for ${item.store.name}.`)}
          />
          <Button ariaLabel={`Reject ${label}`} buttonText="Reject with reason" variant="outlined" size="small" disabled={busy} sx={DANGER_BTN} onClick={() => setRejecting((value) => !value)} />
        </Actions>
      )}
      {rejecting && (
        <Panel title="Why can’t this proof be verified?">
          <TextField label="The vendor reads this" ariaLabel="Certification rejection reason" multiline minRows={2} value={reason} onChange={(event) => setReason(event.target.value)} />
          <Box sx={{ mt: 1.5 }}>
            <Button ariaLabel="Reject certification proof" buttonText="Reject proof" variant="contained" size="small" disabled={busy || !reason.trim()} sx={DANGER_BTN} onClick={() => void run(() => adminApiClient.decideVendorCertification(item.certificationId, { decision: 'rejected', reason: reason.trim() }), `Returned ${label} proof to ${item.vendor.businessName}.`)} />
          </Box>
        </Panel>
      )}
    </Card>
  );
}

